import { gql } from '@apollo/client';
import {
  Button,
  chakra,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Stack,
  useToast,
} from '@chakra-ui/react';
import { t as translate } from 'i18next';
import { useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { InfoAlert } from '~components/ui/Alert';
import { Duration, DurationFields, toDurationMinutes } from '~components/ui/DurationFields';
import { ModalCloseButton } from '~components/ui/ModalCloseButton';
import { fromError } from '~utils/errors';
import { useEnableDeviceLogsMutation } from './__generated__/EnableDeviceLogsModal.graphql';

// The API's hard ceiling (MAX_DEVICE_LOG_DURATION_MINUTES): 30 days.
const MAX_DAYS = 30;
const DEFAULT_DURATION: Duration = { days: 1, hours: 0 };
// How long finished logs are kept before they are deleted.
const LOG_RETENTION_DAYS = 60;

export interface DeviceLogsTarget {
  id: string;
  alias?: string | null;
  serialNumber: string;
}

interface Props {
  display: DeviceLogsTarget | null;
  onClose: () => void;
}

export function EnableDeviceLogsModal({ display, onClose }: Props) {
  const { t } = useTranslation();
  const toast = useToast();
  const [duration, setDuration] = useState<Duration>(DEFAULT_DURATION);
  const [enableDeviceLogs, { loading }] = useEnableDeviceLogsMutation();

  // Every time the modal opens for a display, start from the default duration again.
  useEffect(() => {
    if (display) {
      setDuration(DEFAULT_DURATION);
    }
  }, [display]);

  const durationMinutes = toDurationMinutes(duration);
  const durationError =
    durationMinutes === 0
      ? t('deviceLogsDurationRequired')
      : duration.days === MAX_DAYS && duration.hours > 0
      ? t('deviceLogsDurationTooLong')
      : undefined;

  const handleSubmit = async () => {
    if (!display || durationError) {
      return;
    }

    try {
      await enableDeviceLogs({
        variables: {
          deviceId: display.id,
          durationDays: duration.days,
          durationHours: duration.hours,
        },
      });

      toast({
        status: 'success',
        position: 'bottom',
        title: (
          <Trans
            i18nKey="deviceLogsEnabledTitle"
            values={{ device: display.alias || display.serialNumber }}
            components={{ strong: <strong /> }}
          />
        ),
        description: t('deviceLogsEnabledDescription'),
      });

      onClose();
    } catch (err) {
      toast({
        status: 'error',
        title: t('deviceLogsEnableError'),
        description: fromError(err, 'EnableDeviceLogs', getEnableDeviceLogsErrorMap()),
      });
    }
  };

  return (
    <Modal isOpen={Boolean(display)} onClose={onClose} size="lg">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{t('enableDeviceLogsTitle')}</ModalHeader>
        <ModalCloseButton onClick={onClose} />
        <ModalBody>
          <chakra.p color="gray.600" marginBottom="6">
            {t('enableDeviceLogsDescription')}
          </chakra.p>
          <Stack spacing="6">
            <DurationFields
              value={duration}
              onChange={setDuration}
              maxDays={MAX_DAYS}
              errorMessage={durationError}
            />
            <InfoAlert>{t('deviceLogsRetentionNotice', { count: LOG_RETENTION_DAYS })}</InfoAlert>
          </Stack>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" colorScheme="gray" marginRight="3" onClick={onClose}>
            {t('cancel')}
          </Button>
          <Button
            colorScheme="blue"
            onClick={handleSubmit}
            isLoading={loading}
            isDisabled={Boolean(durationError)}
          >
            {t('enableDeviceLogsButton')}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

function getEnableDeviceLogsErrorMap(): Record<string, string> {
  return {
    SERVICE_ADMIN_DEVICE_LOG_ALREADY_ACTIVE: translate('deviceLogsAlreadyActive'),
    SERVICE_ADMIN_DEVICE_LOG_DURATION_TOO_LONG: translate('deviceLogsDurationTooLong'),
  };
}

EnableDeviceLogsModal.graphql = {
  mutations: {
    EnableDeviceLogs: gql`
      mutation EnableDeviceLogs($deviceId: ID!, $durationDays: Int!, $durationHours: Int!) {
        serviceAdminEnableDeviceLogs(
          deviceId: $deviceId
          durationDays: $durationDays
          durationHours: $durationHours
        ) {
          id
        }
      }
    `,
  },
};
